import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CANDLE_OPTIONS, REACTIVATE_OPTIONS, PRODUCT_TYPE, ORDER_MODE, StrategyFormBuildMode, ChartType, PRICE_METHOD } from '../../strategy.enums';
import { DefineStrategyResultI, AlgoStrategy, defaultAlgoParams, StrategiesService } from '@shared/providers';
import {
    MAX_LOT, MAX_VALUE, MOMENT_DATE_FORMAT, MOMENT_TIME_FORMAT, MIN_ABSOLUTE_VALUE,
    MIN_QTY, Utils, TIME_FORMAT, DEFAULT_PRECISION, MOMENT_DATE_TIME_FORMAT
} from '@app/_helpers';
import { NgxToastrService } from '../../../../providers';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { StrategySetupComponent } from '../strategy-setup/strategy-setup.component';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { TimeRangeErrorMatcher } from '@app/app.models';
import { TimerConfig } from '@app/modules/admin/providers/timer-configuration/timer-configuration.models';
import { ALLOWED_STRATEGY_DAYS } from '../../strategy.constants';

@Component({
    selector: 'app-strategy-define',
    templateUrl: './strategy-define.component.html',
    styleUrls: ['./strategy-define.component.scss']
})
export class StrategyDefineComponent implements OnInit, OnDestroy {
    @Input() strategyMode$: BehaviorSubject<StrategyFormBuildMode> = new BehaviorSubject<StrategyFormBuildMode>(StrategyFormBuildMode.REGULAR);
    @Input() setUpRef: StrategySetupComponent;

    update: AlgoStrategy;
    CANDLE_OPTIONS = CANDLE_OPTIONS;
    REACTIVATE_OPTIONS = REACTIVATE_OPTIONS;
    PRODUCT_TYPE = PRODUCT_TYPE;
    CHART_TYPE = ChartType;
    PRICE_METHOD = PRICE_METHOD;
    StrategyFormBuildMode = StrategyFormBuildMode;

    MAX_LOT = MAX_LOT;
    MAX_VALUE = MAX_VALUE;
    ORDER_MODE = ORDER_MODE;
    minValue = 1;
    maxValue = MAX_LOT

    private subscriptions = new Subscription();
    isOrderQtyEnabled = false;
    showPreferences = false;
    qtyLabel: string;
    matcher = new TimeRangeErrorMatcher();
    timeConf: TimerConfig[] = [];
    selectedExchange: string = '';
    minTime: string='';
    maxTime: string='';
    strategyDefineForm = new UntypedFormGroup({
        orderMode: new UntypedFormControl(null, [Validators.required]),
        isLot: new UntypedFormControl(false, [Validators.required]),
        orderQty: new UntypedFormControl(null, [Validators.required]),
        candleSize: new UntypedFormControl(null, [Validators.required]),
        // orderQty: new FormControl(null, []),
        // productType: new FormControl(null, [Validators.required]),
        // reactive: new FormControl(null, [Validators.required]),
        // automaticStartTime: new FormControl(null, []),
        // automaticShutDownTime: new FormControl(null, []),
        // switchManualy: new FormControl(null, []),
        productType: new UntypedFormControl(null, []),
        chartType: new UntypedFormControl(null, []),
        entryPriceMethod: new UntypedFormControl(null, []),
        exitPriceMethod: new UntypedFormControl(null, []),
        algoCycles: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(999)]),
        rerunAlgo: new UntypedFormControl(null, []),
        switchManually: new UntypedFormControl(null, []),
        startDate: new UntypedFormControl(null, [Validators.required]),
        startTime: new UntypedFormControl(null, [Validators.required]),
        closeDate: new UntypedFormControl(null, [Validators.required]),
        closeTime: new UntypedFormControl(null, [Validators.required]),
        enablePositionProtection: new UntypedFormControl(null, []),
        positionRetakeTime: new UntypedFormControl(null, [Validators.required]),
        positionExitTime: new UntypedFormControl(null, [Validators.required]),
    }, {
        validators: [
            Utils.startEndDateValidator('startDate', 'closeDate', 'startTime', 'closeTime', false, 
                this.minTime, this.maxTime, false, this.strategyMode$?.value)
        ]
    });

    runStrategyEveryDay = new UntypedFormControl(true);
    dayNames = ALLOWED_STRATEGY_DAYS;
    allowedDays = [];

    constructor(
        private snackbar: NgxToastrService,
        private strategyService: StrategiesService
    ) {
        this.strategyService.getTimeConf().subscribe(res => {
            this.timeConf = res;
        });
    }

    ngOnInit() {
        this.subscriptions.add(
            this.strategyMode$.subscribe(isAdvance => {
                this.updateSwitchTimeValidations(!isAdvance);
            })
        );

        this.strategyDefineForm.get('isLot').valueChanges.subscribe(res => {
            if (res) {
                this.strategyDefineForm.get('orderQty').setValue(this.minValue);
            }
            else {
                this.strategyDefineForm.get('orderQty').setValue(this.minValue.toFixed(DEFAULT_PRECISION));
            }
        });
        /**
        * A service to change the close date value everytime the product type is intraday and
        * startdate value is changed.
        * The service will set the value of startdate equal to enddate
        */
        this.strategyDefineForm.get('startDate').valueChanges.subscribe(change => {
            this.strategyDefineForm.get('closeDate').setValue(change);
        })
    }

    toggleOrderQty() {
        this.isOrderQtyEnabled = !this.isOrderQtyEnabled;
        this.strategyDefineForm.get('orderQty').reset();
        this.strategyDefineForm.get('orderQty')
            .setValidators(this.isOrderQtyEnabled ? [Validators.required] : []);
        this.strategyDefineForm.get('orderQty').updateValueAndValidity();
    }

    updateSwitchTimeValidations(manual: boolean) {
        const validations = [];
        this.strategyDefineForm.get('automaticStartTime')?.reset();
        this.strategyDefineForm.get('automaticShutDownTime')?.reset();

        if (!manual) {
            validations.push(Validators.required);
        }

        this.strategyDefineForm.get('automaticStartTime')?.setValidators(validations);
        this.strategyDefineForm.get('automaticShutDownTime')?.setValidators(validations);
        this.strategyDefineForm.get('automaticStartTime')?.updateValueAndValidity();
        this.strategyDefineForm.get('automaticShutDownTime')?.updateValueAndValidity();
    }

    onTradeTypeChange() {
        // This is being called to run the getter to solve sync issues
        const isLot = this.isLot;
        let currentTotalQty = this.strategyDefineForm.get('orderQty').value;
        this.minValue = this.strategyDefineForm.get('isLot').value ? MIN_QTY : MIN_ABSOLUTE_VALUE;
        if (currentTotalQty < this.minValue) {
            currentTotalQty = this.minValue;
        }
        this.strategyDefineForm.get('orderQty').setValue(currentTotalQty);
    }

    setDefaultPreferences(update: AlgoStrategy, val: StrategyFormBuildMode) {

        if (val === StrategyFormBuildMode.REGULAR) {

            this.strategyDefineForm.get('productType').setValue(update.productType);
            this.strategyDefineForm.get('chartType').setValue(update.chartType);
            this.strategyDefineForm.get('entryPriceMethod').setValue(update.entryPriceMethod);
            this.strategyDefineForm.get('exitPriceMethod').setValue(update.exitPriceMethod);
            this.strategyDefineForm.get('algoCycles').setValue(update.algoCycles);

        }
        this.strategyDefineForm.get('rerunAlgo').setValue(update.rerunAlgo);
        this.fillUpdateTime(true)

        this.strategyDefineForm.get('startDate').setValue(moment(update.startDate, MOMENT_DATE_FORMAT).toDate());
        this.strategyDefineForm.get('closeDate').setValue(moment(update.closeDate, MOMENT_DATE_FORMAT).toDate());
        // The enable fields need to be reset everytime when the mode is changed
        this.strategyDefineForm.get('enablePositionProtection').setValue(false);
        this.disableFields(false);
    }


    fillUpdateForm(update: AlgoStrategy) {
        this.strategyDefineForm.patchValue(update);
        this.update = update;
        this.fillUpdateTime(true, update)

        this.strategyDefineForm.get('startDate').setValue(moment(update.startDate, MOMENT_DATE_FORMAT).toDate());
        this.strategyDefineForm.get('closeDate').setValue(moment(update.closeDate, MOMENT_DATE_FORMAT).toDate());
        if (this.update.enablePositionProtection) {
            this.disableFields(true);
        }
        else {
            this.disableFields(false);
        }

        if (update.allowedDays?.length) {
            this.allowedDays = update.allowedDays;
            this.runStrategyEveryDay.setValue(false);
        }
    }

    /**
     * get isLot() calls this function constantly
     * parameter change makes sure values are only updated if something is changed
     * if data already exits then fields should be filled accordingly
     * otherwise, default values should be filled
     * For ESM symbols default time will be 0:00 - 23:59
     * For other exchanges default time will be according to admin
     */
    fillUpdateTime(change: boolean, update?: AlgoStrategy) {
        if (change) {
            if (update) {
                this.strategyDefineForm.get('startTime').setValue(moment(update.startTime, MOMENT_TIME_FORMAT).toDate());
                this.strategyDefineForm.get('closeTime').setValue(moment(update.closeTime, MOMENT_TIME_FORMAT).toDate());
                this.selectedExchange = this.marketId;
            } else {
                const confTime = this.timeConf.find(s => s.market == this.marketId);
                this.selectedExchange = this.marketId;
                if (confTime) {
                    this.strategyDefineForm.get('startTime').setValue(moment(confTime.start_time, MOMENT_TIME_FORMAT).toDate());
                    this.strategyDefineForm.get('closeTime').setValue(moment(confTime.end_time, MOMENT_TIME_FORMAT).toDate());
                } else {
                    this.strategyDefineForm.get('startTime').setValue(moment(defaultAlgoParams.startTime, MOMENT_TIME_FORMAT).toDate());
                    this.strategyDefineForm.get('closeTime').setValue(moment(defaultAlgoParams.closeTime, MOMENT_TIME_FORMAT).toDate());
                }
            }
        }
    }

    disableFields(disable: boolean) {
        if (disable == true) {
            this.strategyDefineForm.get('positionExitTime').setValidators([Validators.required]);
            this.strategyDefineForm.get('positionExitTime').enable();
            this.strategyDefineForm.get('positionRetakeTime').setValidators([Validators.required]);
            this.strategyDefineForm.get('positionRetakeTime').enable();
        }
        else {
            this.strategyDefineForm.get('positionExitTime').setValidators([]);
            this.strategyDefineForm.get('positionExitTime').disable();
            this.strategyDefineForm.get('positionRetakeTime').setValidators([]);
            this.strategyDefineForm.get('positionRetakeTime').disable();
        }
    }

    changesPositionSecurity(event: MatCheckboxChange) {
        if (event.checked) {
            this.disableFields(true);
        }
        else {
            this.disableFields(false);
        }
    }

    getData(): DefineStrategyResultI {
        const data = this.strategyDefineForm.getRawValue() as DefineStrategyResultI;
        if (this.strategyMode$.value === StrategyFormBuildMode.REGULAR) {
            this.defaultRegularStrategy(data);
        }
        this.strategyDefineForm.markAllAsTouched();
        const confTime = this.timeConf.find(s => s.market == this.marketId);
        if (!this.isValid) { return; }
        // Validations are being done dynamically
        if (this.strategyMode$.value !== StrategyFormBuildMode.WIZARD) {
            // In Case of regular and PRO mode always send default date/time.

            if (confTime) {
                data.startTime = confTime.start_time;
                data.closeTime = confTime.end_time;
            } else {
                data.startTime = defaultAlgoParams.startTime;
                data.closeTime = defaultAlgoParams.closeTime;
            }
            data.startDate = defaultAlgoParams.startDate;
            data.closeDate = defaultAlgoParams.closeDate;
        } else {
            if (data.startDate) {
                const startDate = moment(data.startDate);
                if (startDate <= moment()) {
                    data.startDate = moment().format(MOMENT_DATE_FORMAT);
                }
                else {
                    data.startDate = startDate.format(MOMENT_DATE_FORMAT);
                }
                const startTime = moment(data.startTime);
                data.startTime = startTime.format(MOMENT_TIME_FORMAT);
            }
            if (data.closeTime) {
                const closeDate = moment(data.closeDate);
                if (closeDate <= moment()) {
                    data.closeDate = moment().format(MOMENT_DATE_FORMAT)
                }
                else {
                    data.closeDate = closeDate.format(MOMENT_DATE_FORMAT);
                }
                const closeTime = moment(data.closeTime);
                data.closeTime = closeTime.format(MOMENT_TIME_FORMAT);
            }
            // If wizard mode and run everyday is not selected and there are some specifc days selected for algo use that.
            // Otherwise, HFT will handle default
            if (!this.runStrategyEveryDay.value && this.allowedDays.length) {
                data.allowedDays = this.allowedDays;
            }
        }

        if (confTime) {
            data.positionExitTime = moment(confTime.end_time, MOMENT_TIME_FORMAT).unix() * 1000;
            data.positionRetakeTime = moment(confTime.start_time, MOMENT_TIME_FORMAT).unix() * 1000;
        } else {
            data.positionExitTime = moment(data.positionExitTime).unix() * 1000;
            data.positionRetakeTime = moment(data.positionRetakeTime).unix() * 1000;
        }

        return data;

    }

    runningDayChanged(event: MatCheckboxChange) {
        const dayIdx = ALLOWED_STRATEGY_DAYS.indexOf(event.source.value) + 1;

        // If checked, add to allowed days
        if (event.checked) {
            this.allowedDays.push(dayIdx);
        } else {
            // Otherwise, remove from allowed days.
            this.allowedDays.splice(this.allowedDays.indexOf(dayIdx), 1);
        }
    }

    defaultRegularStrategy(data:DefineStrategyResultI){
        data.productType = PRODUCT_TYPE.INTRADAY;
        data.chartType = ChartType.CANDLESTICK;
        data.entryPriceMethod = PRICE_METHOD.MARKET_ORDER;
        data.exitPriceMethod = PRICE_METHOD.MARKET_ORDER;
        data.algoCycles = 1;
        data.rerunAlgo = REACTIVATE_OPTIONS.never;
    }

    get isWizardMode() {
        return this.strategyMode$.pipe(
            map(val => val === StrategyFormBuildMode.WIZARD)
        )
    }

    get isCM() {
        if (this.setUpRef?.symbols?.length
            && this.setUpRef.symbols[0].market_id.endsWith('CM')) { return true; }
        else false;
    }

    get marketId() {
        if (this.setUpRef?.symbols?.length) {
            return this.setUpRef.symbols[0].market_id;
        } else {
            return '';
        }
    }

    get pickerType() {
        return this.strategyDefineForm.get('productType').value === PRODUCT_TYPE.DELIVERY ? 'calendar' : 'timer';
    }

    get isLot() {
        const isLot = this.strategyDefineForm.get('isLot').value;
        this.minValue = isLot ? MIN_QTY : MIN_ABSOLUTE_VALUE;
        this.maxValue = isLot ? MAX_LOT : MAX_VALUE;
        this.qtyLabel = isLot ? this.isCM ? 'Quantity' : 'Lots' : 'Value';
        const confTime = this.timeConf.find(s => s.market == this.marketId);
        this.minTime = moment(defaultAlgoParams.startTime, MOMENT_TIME_FORMAT).format(TIME_FORMAT);
        this.maxTime = moment(defaultAlgoParams.closeTime, MOMENT_TIME_FORMAT).format(TIME_FORMAT);
        if (confTime) {
            this.minTime = moment(confTime.start_time, TIME_FORMAT + ':ss').format(TIME_FORMAT);
            this.maxTime = moment(confTime.end_time, TIME_FORMAT + ':ss').format(TIME_FORMAT);
        }
        this.strategyDefineForm.setValidators(Utils.startEndDateValidator('startDate', 'closeDate', 'startTime', 'closeTime', false,
            this.minTime, this.maxTime, false, this.strategyMode$?.value))
        this.strategyDefineForm.get('orderQty').setValidators
            ([Validators.max(this.maxValue), Validators.min(this.minValue), Validators.required]);
        this.fillUpdateTime(this.selectedExchange != this.marketId);
        return isLot;
    }

    get isValid() {
        return this.strategyDefineForm.valid;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
